/**
 * Shared working-state UI for agent chat surfaces.
 *
 * Grammar: chatting, thinking, and working are visually distinct states. Prose streams in
 * plain message bubbles; thinking is one muted pulsing line; working is a quiet step trace
 * whose active row carries the single point of brand color (blue = acting now) and whose
 * labels flip from present to past tense on success. Yellow/red appear only when a step
 * needs attention. No containers, no status cards, no durations.
 *
 * This module is surface-agnostic: steps arrive with labels already resolved, so any agent
 * Assistant surfaces and future governed tools can reuse it unchanged.
 */

import { IconCheck, IconLock, IconRefresh, IconX } from "@tabler/icons-react";
import { useRef } from "react";
import { GRAY_PALETTE } from "~/styles/palettes";
import { Box, Flex, Loader, Text } from "~/ui";
import type { ToolActivityState, ToolActivityStep } from "./agent-activity-state";

export type { ToolActivityEntry, ToolActivityState, ToolActivityStep } from "./agent-activity-state";
export {
  appendToolActivityStep,
  clearActivitySteps,
  failActiveSteps,
  hasVisibleActivityTrace,
  resolveToolActivityStep,
} from "./agent-activity-state";

const toolActivityStateConfig = (
  state: ToolActivityState,
): { iconColor: string; textColor: string; ariaSuffix: string; icon: React.ReactNode } => {
  switch (state) {
    case "active":
      // The trace's single point of brand color: blue = acting now (thinking stays gray; problems stay yellow/red)
      return { iconColor: "blue.6", textColor: "gray.7", ariaSuffix: "in progress", icon: <Loader size={12} color="blue" /> };
    case "succeeded":
      return { iconColor: "gray.5", textColor: "gray.6", ariaSuffix: "done", icon: <IconCheck size={12} /> };
    case "needs_adjustment":
      // Neutral, not a warning: a corrected retry is normal model behavior, not a problem the person needs to see as red/yellow.
      return { iconColor: "gray.6", textColor: "gray.6", ariaSuffix: "refining", icon: <IconRefresh size={12} /> };
    case "blocked":
      return { iconColor: "red.7", textColor: "red.7", ariaSuffix: "blocked", icon: <IconLock size={12} /> };
    case "failed":
      return { iconColor: "red.7", textColor: "red.7", ariaSuffix: "failed", icon: <IconX size={12} /> };
  }
};

const ToolActivityRow = ({ step }: { step: ToolActivityStep }) => {
  const config = toolActivityStateConfig(step.state);
  const tenseLabel = step.state === "succeeded" ? step.doneLabel : step.label;
  const label = tenseLabel;
  return (
    <Flex align="center" gap="xs" title={step.title} aria-label={`${label} - ${config.ariaSuffix}`}>
      <Box c={config.iconColor} style={{ display: "flex", alignItems: "center" }}>
        {config.icon}
      </Box>
      <Text size="xs" c={config.textColor}>
        {label}
      </Text>
    </Flex>
  );
};

export const AgentActivityTrace = ({ steps }: { steps: ToolActivityStep[] }) => {
  if (steps.length === 0) {
    return null;
  }
  return (
    <Flex direction="column" gap={4} aria-label="Agent activity" style={{ maxHeight: 180, overflowY: "auto" }}>
      {steps.map(step => (
        <ToolActivityRow key={step.id} step={step} />
      ))}
    </Flex>
  );
};

/**
 * The single live-thinking affordance: a muted line whose text carries a slow shimmer sweep
 * (the standard "model is thinking" signal) and fades in on label change. Reduced motion falls
 * back to static muted text with the loader as the sole progress indicator.
 */
export const AgentThinkingLine = ({ label }: { label: string }) => {
  const fadeGenerationRef = useRef(0);
  const previousLabelRef = useRef(label);
  if (previousLabelRef.current !== label) {
    fadeGenerationRef.current += 1;
    previousLabelRef.current = label;
  }
  const fadeClass = fadeGenerationRef.current % 2 === 0 ? "agent-thinking-fade-a" : "agent-thinking-fade-b";
  return (
    <Flex align="center" gap="xs">
      <Loader size={12} color="gray" />
      <Text size="sm" className={`agent-thinking-shimmer ${fadeClass}`}>
        {label}
      </Text>
      <style>{`
      /* Raw CSS colors in the assistant surfaces come from ~/styles/palettes, one source, never
         --mantine-color-* variables: a var that resolves to nothing voids the gradient while the
         transparent text fill still applies, rendering the label invisible. */
      .agent-thinking-shimmer {
        background: linear-gradient(
          90deg,
          ${GRAY_PALETTE[6]} 0%,
          ${GRAY_PALETTE[6]} 35%,
          ${GRAY_PALETTE[4]} 50%,
          ${GRAY_PALETTE[6]} 65%,
          ${GRAY_PALETTE[6]} 100%
        );
        background-size: 200% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
      }
      .agent-thinking-shimmer.agent-thinking-fade-a {
        animation: agent-thinking-fade-a 0.2s ease, agent-thinking-shimmer 2.2s linear infinite;
      }
      .agent-thinking-shimmer.agent-thinking-fade-b {
        animation: agent-thinking-fade-b 0.2s ease, agent-thinking-shimmer 2.2s linear infinite;
      }
      @keyframes agent-thinking-shimmer {
        from { background-position: 200% 0; }
        to { background-position: -200% 0; }
      }
      @keyframes agent-thinking-fade-a {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes agent-thinking-fade-b {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .agent-thinking-shimmer {
          animation: none;
          background: none;
          -webkit-text-fill-color: ${GRAY_PALETTE[6]};
          color: ${GRAY_PALETTE[6]};
        }
      }
    `}</style>
    </Flex>
  );
};
